/**
 * Tech phone view (scope §5.11, mockup 05). Owner: web-phone-pwa (slice 19).
 *
 * Single pane: My day (cases I own; overdue first, then due today, then the rest), the case with its audience
 * tabs as a swipe strip, and a sticky bar of three big buttons (Log time, Next move, Needs on-site) plus the
 * camera. One or two taps per action, no key chords, no composer by default: reply is a second screen.
 * Offline it shows the service worker's saved reads and turns every write off.
 */
import { Avatar, BottomTabs, Button, cn, EmptyState, ErrorBanner, Skeleton, Wordmark } from '@patchbay/ui'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate, useSearch } from '@tanstack/react-router'
import { Calendar, ChevronDown, ChevronRight, FileText, Share, Sparkles, User, WifiOff, X } from 'lucide-react'
import * as React from 'react'
import { unwrap } from '../lib/api/client'
import { stateApiV1VisitsVisitIdStatePost, type TodayHygiene, type VisitOut, visitsApiV1VisitsGet } from '../lib/api/generated'
import { formatWhen } from '../lib/display-time'
import { formatOfficeWindow } from '../lib/toronto-time'
import { CaseSummaryContent, MissingCaseSummary, structuredCaseSummary } from './case-summary'
import { HelpMenu } from './help/help-menu'
import { ActionBar, type ActionTarget, type BarAction, LogTimeSheet, NeedsOnsiteSheet, NextMoveSheet } from './mobile/actions'
import {
  buildCaseSummary,
  type CaseRow,
  caseDetailQuery,
  describeError,
  getJson,
  type LaneResult,
  livenessQuery,
  type MeOut,
  meQuery,
  myLanesQuery,
  rememberCsrf,
  takeCase,
  threadsQuery,
  unassignedLanesQuery,
} from './mobile/api'
import { audienceLabel, PhoneCase } from './mobile/case'
import { addLaneCase, buildMyDay, type DayItem, formatDue, primaryTicket, removeLaneCase, staleLabel } from './mobile/my-day-data'
import { useIosInstallHint, useOnline } from './mobile/pwa'
import { PhoneReply } from './mobile/reply'
import { MyProjectTasks } from './projects/my-tasks'
import { VisitProjectMark } from './projects/project-mark'
import { UnloggedTimeCard } from './time/unlogged-card'

export interface MyDaySearch {
  case?: string
  reply?: string
}

export function validateMyDaySearch(raw: Record<string, unknown>): MyDaySearch {
  const id = (v: unknown) => (typeof v === 'string' && /^[0-9a-fA-F-]{1,64}$/.test(v) ? v : undefined)
  return { case: id(raw.case), reply: id(raw.reply) }
}

export const OFFLINE_REASON = 'Offline: showing saved data. Log time, Next move, Needs on-site and Reply are off until you reconnect.'

function targetOf(row: { id: string; number: string; title: string; tickets: CaseRow['tickets'] }): ActionTarget {
  return { caseId: row.id, number: row.number, title: row.title, ticket: primaryTicket(row.tickets) }
}

function IosInstallHint() {
  const hint = useIosInstallHint()
  if (!hint.show) return null
  return (
    <div className="mx-4 mb-3 flex items-start gap-2 rounded-lg border border-primary/30 bg-primary-soft px-3 py-2 text-sm" role="note">
      <Share className="mt-0.5 size-4 shrink-0 text-primary" />
      <p className="flex-1">
        Install Patchbay: tap <b>Share</b>, then <b>Add to Home Screen</b>.
      </p>
      <button type="button" aria-label="Dismiss install hint" className="cursor-pointer p-1 text-muted-foreground" onClick={hint.dismiss}>
        <X className="size-4" />
      </button>
    </div>
  )
}

function DayCard({
  item,
  open,
  selected,
  onToggle,
  onOpenCase,
}: {
  item: DayItem
  open: boolean
  selected: boolean
  onToggle: () => void
  onOpenCase: () => void
}) {
  const r = item.row
  const queryClient = useQueryClient()
  const summary = structuredCaseSummary(r.case_summary)
  const buildSummary = async () => {
    await buildCaseSummary(r.id)
    await queryClient.invalidateQueries({ queryKey: ['my-day'] })
  }
  return (
    <li
      data-selected={selected || undefined}
      className={selected ? 'rounded-xl border-2 border-primary/60 bg-primary-soft/40' : 'rounded-xl border border-border bg-card'}
    >
      <button type="button" onClick={onToggle} aria-expanded={open} className="flex w-full cursor-pointer items-start gap-3 p-4 text-left">
        <span
          className={cn(
            'mt-1.5 size-3 shrink-0 rounded-full',
            item.bucket === 'overdue' ? 'bg-destructive' : open ? 'bg-primary' : 'bg-subtle',
          )}
          aria-hidden
        />
        <span className="min-w-0 flex-1">
          <span className={cn('tabular block text-base font-semibold', item.bucket === 'overdue' && 'text-destructive')}>
            {item.bucket === 'overdue' ? 'Overdue · ' : ''}
            {formatDue(item.due)}
          </span>
          <span className="block truncate text-lg font-semibold">{r.customer_display ?? 'Customer not set'}</span>
          <span className="block text-base">{r.next_move?.text ?? r.title}</span>
          <span className="block truncate text-sm text-muted-foreground">
            {r.number} · {r.title}
          </span>
        </span>
        {open ? <ChevronDown className="size-5 text-primary" /> : <ChevronRight className="size-5 text-subtle" />}
      </button>
      {open ? (
        <div className="space-y-3 border-t border-border px-4 pt-3 pb-4">
          <h2 className="flex items-center gap-1.5 text-sm font-semibold">
            <Sparkles className="size-4 text-riley" /> Riley's summary
          </h2>
          <div className="rounded-lg border border-border bg-card p-3 text-base">
            {summary ? <CaseSummaryContent summary={summary} /> : <MissingCaseSummary onBuild={buildSummary} />}
          </div>
          <Button className="h-12 w-full text-base" onClick={onOpenCase}>
            <FileText className="size-5" /> Open case
          </Button>
        </div>
      ) : null}
    </li>
  )
}

function HygienePinned() {
  const q = useQuery({
    queryKey: ['home', 'today', 'hygiene'],
    queryFn: async (): Promise<TodayHygiene> => (await getJson<TodayHygiene>('/home/today/hygiene')).data,
    staleTime: 30_000,
    retry: 1,
  })
  const hygiene = q.data
  const sections = hygiene?.sections ?? []
  if (q.isPending) return <Skeleton className="h-28 w-full" />
  if (q.isError) {
    return (
      <ErrorBanner
        tone="warning"
        title="Attention list is unreachable"
        detail="Nothing matched cannot be assumed; retry before deciding there are no hygiene items."
        onRetry={() => void q.refetch()}
      />
    )
  }
  if (!hygiene?.total) return null
  return (
    <section aria-label="Needs attention" className="space-y-3 rounded-xl border-2 border-warning/40 bg-warning-soft/30 p-3">
      <h2 className="text-sm font-semibold tracking-wide text-warning uppercase">
        Needs attention <span className="tabular">({hygiene.total})</span>
      </h2>
      {sections.map((section) => (
        <div key={section.rule_id} className="space-y-2">
          <h3 className="text-sm font-semibold">{section.label}</h3>
          <ol className="space-y-2">
            {section.rows.map((row) => (
              <li key={`${section.rule_id}:${row.case_id}`} className="rounded-lg border border-border bg-card p-3">
                <p className="font-semibold">{row.customer_display ?? 'Customer not resolved'}</p>
                <p className="text-sm text-muted-foreground">
                  {row.number} · {row.title}
                </p>
                <div className="mt-2 flex flex-wrap gap-2 text-sm">
                  <a className="font-medium text-primary underline-offset-2 hover:underline" href={row.actions.set_next_move}>
                    Set next move
                  </a>
                  <a className="font-medium text-primary underline-offset-2 hover:underline" href={row.actions.take_ownership}>
                    Take ownership
                  </a>
                  <a className="font-medium text-primary underline-offset-2 hover:underline" href={row.actions.reply}>
                    Reply
                  </a>
                  <a className="font-medium text-primary underline-offset-2 hover:underline" href={row.actions.snooze}>
                    Snooze
                  </a>
                </div>
              </li>
            ))}
          </ol>
        </div>
      ))}
    </section>
  )
}

function visitLabel(visit: VisitOut): string {
  const label = formatOfficeWindow(visit.start_at, visit.window_kind)
  return label === 'Window not supplied' ? visit.status.replaceAll('_', ' ') : label
}

function TodayVisits({ meId }: { meId: string }) {
  const now = new Date()
  const dayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0)
  const dayEnd = new Date(dayStart)
  dayEnd.setDate(dayEnd.getDate() + 1)
  const q = useQuery({
    queryKey: ['my-day', 'visits', meId, dayStart.toISOString()],
    queryFn: () =>
      unwrap(
        visitsApiV1VisitsGet({
          query: {
            user_id: meId,
            date_from: dayStart.toISOString(),
            date_to: dayEnd.toISOString(),
            limit: 100,
          },
        }),
      ),
    staleTime: 20_000,
  })
  const qc = useQueryClient()
  const change = useMutation({
    mutationFn: ({ visitId, status, eta }: { visitId: string; status: VisitOut['status']; eta?: number }) =>
      unwrap(
        stateApiV1VisitsVisitIdStatePost({
          path: { visit_id: visitId },
          headers: { 'Idempotency-Key': `myday-visit:${visitId}:${status}:${crypto.randomUUID()}` },
          body: {
            initiated_by: 'human',
            status,
            eta_minutes: eta ?? null,
            reason:
              status === 'done'
                ? 'Completed from My Day'
                : status === 'in_progress'
                  ? 'Checked in from My Day'
                  : 'Heading over from My Day',
          },
        }),
      ),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['my-day', 'visits'] })
      await qc.invalidateQueries({ queryKey: ['my-day'] })
    },
  })
  if (q.isPending) return <Skeleton className="h-24 w-full" />
  if (q.isError) {
    return (
      <ErrorBanner
        tone="warning"
        title="Today's visits are unreachable"
        detail="Retry before deciding there are no visits."
        onRetry={() => void q.refetch()}
      />
    )
  }
  const items = (q.data?.items ?? []).filter((visit) => ['booked', 'confirmed', 'on_the_way', 'in_progress'].includes(visit.status))
  if (items.length === 0) return null
  return (
    <section aria-label="Today's visits" className="mb-4 space-y-2 rounded-xl border border-border bg-card p-3">
      <h2 className="text-sm font-semibold tracking-wide text-muted-foreground uppercase">
        Today's visits <span className="tabular">({items.length})</span>
      </h2>
      <ol className="space-y-2">
        {items.map((visit) => (
          <li key={visit.id} className="rounded-lg border border-border bg-background p-2.5">
            <p className="text-sm font-semibold">{visitLabel(visit)}</p>
            <VisitProjectMark visit={visit} />
            <p className="text-xs text-muted-foreground">
              Case {visit.case_id.slice(0, 8)} · {visit.status.replaceAll('_', ' ')}
            </p>
            <div className="mt-2 flex flex-wrap gap-2">
              <Button
                size="sm"
                variant="outline"
                disabled={change.isPending}
                onClick={() => change.mutate({ visitId: visit.id, status: 'on_the_way', eta: 30 })}
              >
                Heading over
              </Button>
              <Button
                size="sm"
                variant="outline"
                disabled={change.isPending}
                onClick={() => change.mutate({ visitId: visit.id, status: 'in_progress' })}
              >
                Check in
              </Button>
              <Button size="sm" disabled={change.isPending} onClick={() => change.mutate({ visitId: visit.id, status: 'done' })}>
                Done
              </Button>
            </div>
          </li>
        ))}
      </ol>
    </section>
  )
}

function DayList({
  me,
  selected,
  onSelect,
  onOpenCase,
}: {
  me: MeOut
  selected: string | null
  onSelect: (row: CaseRow | null) => void
  onOpenCase: (id: string) => void
}) {
  const q = useQuery(myLanesQuery(me.id))
  const unassigned = useQuery(unassignedLanesQuery())
  const qc = useQueryClient()
  const mineKey = myLanesQuery(me.id).queryKey
  const unassignedKey = unassignedLanesQuery().queryKey
  const take = useMutation({
    mutationFn: (caseId: string) => takeCase(caseId, me.id),
    onMutate: async (caseId) => {
      await Promise.all([qc.cancelQueries({ queryKey: mineKey }), qc.cancelQueries({ queryKey: unassignedKey })])
      const beforeMine = qc.getQueryData<LaneResult[]>(mineKey)
      const beforeUnassigned = qc.getQueryData<LaneResult[]>(unassignedKey)
      const removed = removeLaneCase(beforeUnassigned, caseId)
      qc.setQueryData(unassignedKey, removed.lanes)
      const optimistic = removed.taken ? { ...removed.taken.row, owner: { id: me.id, name: me.name, email: me.email } } : undefined
      qc.setQueryData(mineKey, addLaneCase(beforeMine, removed.taken, optimistic))
      return { beforeMine, beforeUnassigned, taken: removed.taken }
    },
    onSuccess: (rows, _caseId, context) => {
      const authoritative = rows[0]
      if (authoritative) qc.setQueryData<LaneResult[]>(mineKey, (current) => addLaneCase(current, context?.taken, authoritative))
    },
    onError: (_error, _caseId, context) => {
      qc.setQueryData(mineKey, context?.beforeMine)
      qc.setQueryData(unassignedKey, context?.beforeUnassigned)
    },
    onSettled: async () => {
      await Promise.all([
        qc.invalidateQueries({ queryKey: mineKey }),
        qc.invalidateQueries({ queryKey: unassignedKey }),
        qc.invalidateQueries({ queryKey: ['queue'] }),
      ])
    },
  })
  const day = React.useMemo(() => (q.data ? buildMyDay(q.data, me.id) : null), [q.data, me.id])
  const [showLater, setShowLater] = React.useState(false)
  // Collapsing a card keeps it the bar's target; only choosing another case moves the target.
  const [expanded, setExpanded] = React.useState<string | null>(selected)
  const first = day ? (day.overdue[0] ?? day.today[0] ?? day.later[0] ?? null) : null
  // Two taps to log time: the first case is already chosen, so the bar's Log time opens straight onto it.
  React.useEffect(() => {
    if (!day) return
    const all = [...day.overdue, ...day.today, ...day.later]
    if (!selected || !all.some((i) => i.row.id === selected)) {
      onSelect(first?.row ?? null)
      setExpanded(first?.row.id ?? null)
    }
  }, [day, selected, first, onSelect])
  const unassignedRows = React.useMemo(() => {
    const byId = new Map<string, CaseRow>()
    for (const lane of unassigned.data ?? []) for (const row of lane.rows ?? []) byId.set(row.id, row)
    return [...byId.values()].sort((a, b) => a.number.localeCompare(b.number))
  }, [unassigned.data])

  if (q.isError) return <ErrorBanner title="My day is unreachable" detail={describeError(q.error)} onRetry={() => void q.refetch()} />
  if (!day) return <Skeleton className="h-40 w-full" />
  const stale = staleLabel(day.servedAt)
  const section = (title: string, items: DayItem[]) =>
    items.length ? (
      <section aria-label={title} className="space-y-2">
        <h2 className="text-sm font-semibold tracking-wide text-muted-foreground uppercase">
          {title} <span className="tabular">({items.length})</span>
        </h2>
        <ol className="space-y-3">
          {items.map((i) => (
            <DayCard
              key={i.row.id}
              item={i}
              open={expanded === i.row.id}
              selected={selected === i.row.id}
              onToggle={() => {
                onSelect(i.row)
                setExpanded(expanded === i.row.id ? null : i.row.id)
              }}
              onOpenCase={() => onOpenCase(i.row.id)}
            />
          ))}
        </ol>
      </section>
    ) : null
  const nothing = day.overdue.length + day.today.length + day.later.length === 0
  return (
    <div className="space-y-4">
      <HygienePinned />
      {stale ? <p className="text-xs text-warning">{stale}</p> : null}
      {day.unreachable.length ? (
        <ErrorBanner
          tone="warning"
          title="Some of your cases could not be loaded"
          detail={`${day.unreachable.map((u) => u.lane.replace(/_/g, ' ')).join(', ')}: unreachable. This list may be missing cases.`}
        />
      ) : null}
      {section('Overdue', day.overdue)}
      {section('Due today', day.today)}
      {day.later.length ? (
        showLater ? (
          section('Later', day.later)
        ) : (
          <Button variant="ghost" className="h-12 w-full" onClick={() => setShowLater(true)}>
            {day.later.length} more of your cases, not due today
          </Button>
        )
      ) : null}
      {nothing && !day.unreachable.length ? (
        <EmptyState icon={<Calendar />} title="Nothing on your plate today." detail="No open case is owned by you." />
      ) : null}
      <section aria-label="Unassigned pool" className="space-y-2 border-t border-border pt-4">
        <h2 className="text-sm font-semibold tracking-wide text-muted-foreground uppercase">
          Unassigned pool <span className="tabular">({unassignedRows.length})</span>
        </h2>
        {unassigned.isError || unassigned.data?.some((lane) => lane.error) ? (
          <ErrorBanner
            tone="warning"
            title="Unassigned pool is unreachable"
            detail="Nothing matched cannot be assumed; retry before deciding the pool is empty."
            onRetry={() => void unassigned.refetch()}
          />
        ) : unassigned.isPending ? (
          <Skeleton className="h-24 w-full" />
        ) : unassignedRows.length ? (
          <ol className="space-y-2">
            {unassignedRows.map((row) => (
              <li key={row.id} className="rounded-xl border border-border bg-card p-3">
                <p className="truncate text-base font-semibold">{row.customer_display ?? 'Customer not set'}</p>
                <p className="text-sm">{row.next_move?.text ?? row.title}</p>
                <p className="truncate text-xs text-muted-foreground">
                  {row.number} · {row.title}
                </p>
                <div className="mt-3 grid grid-cols-2 gap-2">
                  <Button className="h-11" disabled={take.isPending} onClick={() => take.mutate(row.id)}>
                    {take.isPending && take.variables === row.id ? 'Taking…' : 'Take'}
                  </Button>
                  <Button className="h-11" variant="outline" onClick={() => onOpenCase(row.id)}>
                    Open
                  </Button>
                </div>
              </li>
            ))}
          </ol>
        ) : (
          <p className="text-sm text-muted-foreground">No unassigned cases.</p>
        )}
      </section>
    </div>
  )
}

export function MyDayScreen() {
  const search = useSearch({ strict: false }) as MyDaySearch
  const navigate = useNavigate()
  const online = useOnline()
  const meQ = useQuery(meQuery())
  const live = useQuery(livenessQuery())
  const me = meQ.data?.data
  React.useEffect(() => rememberCsrf(me?.csrf_token), [me?.csrf_token])
  const [selectedRow, setSelectedRow] = React.useState<CaseRow | null>(null)
  const [action, setAction] = React.useState<BarAction | null>(null)

  const caseId = search.case ?? null
  const caseQ = useQuery({ ...caseDetailQuery(caseId ?? ''), enabled: Boolean(caseId) })
  const threadsQ = useQuery({ ...threadsQuery(caseId ?? ''), enabled: Boolean(caseId && search.reply) })

  const writeBlock = !online ? OFFLINE_REASON : me?.role === 'billing' ? 'The billing role writes nothing to Desk.' : null
  const liveness = live.data?.data
  const sendBlock = writeBlock ?? (liveness?.sends_disabled ? (liveness.sends_disabled_reason ?? 'Mirror stale; sends disabled.') : null)

  const caseDetail = caseQ.data?.data
  const target: ActionTarget | null = caseId ? (caseDetail ? targetOf(caseDetail) : null) : selectedRow ? targetOf(selectedRow) : null

  const go = (s: MyDaySearch) => navigate({ to: '/my-day', search: s as never })
  const onSelect = React.useCallback((row: CaseRow | null) => setSelectedRow(row), [])
  const replyThread = search.reply ? threadsQ.data?.data.find((t) => t.id === search.reply) : undefined

  let body: React.ReactNode
  if (meQ.isError) {
    body = (
      <div className="px-4">
        <ErrorBanner title="Can't load who you are" detail={describeError(meQ.error)} onRetry={() => void meQ.refetch()} />
        <Link to="/" className="mt-3 block text-primary">
          Go to sign-in
        </Link>
      </div>
    )
  } else if (!me) {
    body = (
      <div className="px-4">
        <Skeleton className="h-40 w-full" />
      </div>
    )
  } else if (caseId && search.reply && target) {
    body = (
      <PhoneReply
        threadId={search.reply}
        audienceLabel={replyThread ? audienceLabel(replyThread) : 'this conversation'}
        target={target}
        sendBlock={sendBlock}
        onBack={() => go({ case: caseId })}
      />
    )
  } else if (caseId) {
    body = <PhoneCase caseId={caseId} onBack={() => go({})} onReply={(threadId) => go({ case: caseId, reply: threadId })} />
  } else {
    body = (
      <div className="px-4 pb-4">
        <TodayVisits meId={me.id} />
        <MyProjectTasks />
        <UnloggedTimeCard big className="mb-4" onOpenCase={(r) => go({ case: r.case_id })} />
        <DayList me={me} selected={selectedRow?.id ?? null} onSelect={onSelect} onOpenCase={(id) => go({ case: id })} />
      </div>
    )
  }

  const today = formatWhen(new Date().toISOString(), { style: 'date' })
  return (
    <div className="mx-auto flex h-dvh max-w-md flex-col border-x border-border bg-background" data-view="phone">
      <header className="flex h-14 shrink-0 items-center gap-2 px-4 pt-[env(safe-area-inset-top)]">
        <Wordmark />
        <span className="ml-auto flex items-center gap-2">
          <HelpMenu />
          {me ? <Avatar name={me.name} size="sm" /> : null}
        </span>
      </header>
      {!online ? (
        <div
          className="mx-4 mb-2 flex items-start gap-2 rounded-lg border border-warning/40 bg-warning-soft px-3 py-2 text-sm text-warning"
          role="status"
        >
          <WifiOff className="mt-0.5 size-4 shrink-0" />
          <p>{OFFLINE_REASON}</p>
        </div>
      ) : null}
      {me && me.desk_token !== 'connected' && me.role !== 'billing' ? (
        <p className="mx-4 mb-2 rounded-lg border border-warning/40 bg-warning-soft px-3 py-2 text-sm text-warning">
          Desk is not connected. Time you log is kept and reaches Desk when you{' '}
          <Link to="/me" className="underline">
            connect Desk
          </Link>
          .
        </p>
      ) : null}
      <IosInstallHint />
      <main className="min-h-0 flex-1 overflow-y-auto">
        {!caseId ? <h1 className="px-4 pb-3 text-3xl font-bold">My day · {today}</h1> : null}
        {body}
      </main>
      {me && !search.reply ? <ActionBar target={target} writeBlock={writeBlock} onAction={setAction} /> : null}
      {me && target ? (
        <>
          <LogTimeSheet
            open={action === 'time'}
            onOpenChange={(o) => setAction(o ? 'time' : null)}
            target={target}
            writeBlock={writeBlock}
          />
          <NextMoveSheet
            open={action === 'next'}
            onOpenChange={(o) => setAction(o ? 'next' : null)}
            target={target}
            me={me}
            writeBlock={writeBlock}
          />
          <NeedsOnsiteSheet
            open={action === 'onsite'}
            onOpenChange={(o) => setAction(o ? 'onsite' : null)}
            target={target}
            writeBlock={writeBlock}
          />
        </>
      ) : null}
      <BottomTabs
        items={[
          { id: 'day', label: 'My day', icon: <Calendar />, active: !caseId, onSelect: () => go({}) },
          {
            id: 'case',
            label: 'Case',
            icon: <FileText />,
            active: Boolean(caseId),
            onSelect: () => {
              const id = caseId ?? selectedRow?.id
              if (id) go({ case: id })
            },
          },
          { id: 'me', label: 'Me', icon: <User />, onSelect: () => navigate({ to: '/me' }) },
        ]}
      />
    </div>
  )
}
